/**
 * Visual theme packs for the 3D putt scene. A theme is pure data — every knob
 * maps onto an existing parameter of PuttScene (sky shader uniforms, fog,
 * light rig, ground-shader zone tints, horizon silhouette texture, clouds),
 * so themes cost nothing at runtime beyond the values themselves.
 *
 * Used by Simulated 9 to give each course a distinct look; Target/Challenge
 * render CLASSIC_THEME.
 */

export type HorizonKind =
  | 'trees'
  | 'pines'
  | 'palms'
  | 'mesa'
  | 'skyline'
  | 'dunes'
  | 'peaks';

export interface Putt3dTheme {
  id: string;
  /**
   * The first ten Sim9 courses intentionally retain the established renderer.
   * Legacy theme-level opt-in retained for Cedar Ridge. Courses 12–20 select
   * their realistic biome through course.sceneryId while reusing these palettes.
   */
  visualStyle?: 'classic' | 'realistic';
  /** Sky dome gradient. */
  skyTop: number;
  skyBottom: number;
  /** Scene fog color — should match skyBottom family for a seamless horizon. */
  fogColor: number;
  /** GL clear color behind everything. */
  background: number;
  /** Sun glow sprite. */
  sunColor: number;
  sunPos: [number, number, number];
  sunScale: number;
  /** Key light (azimuth is derived from sunPos so shadows track the sun). */
  lightColor: number;
  lightIntensity: number;
  /** Hemisphere fill. */
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  /** Ground material tint (the green). */
  greenTint: number;
  /** Shader zone multipliers (RGB 0..~1.1). */
  roughTint: [number, number, number];
  fringeTint: [number, number, number];
  /** Horizon silhouette ring. */
  horizon: HorizonKind;
  /** Legacy flat tint — superseded by horizonPalette (kept for reference). */
  horizonColor: number;
  /** Colors baked into the horizon texture: dark/mid/light body tones +
   *  an accent (trunks, cactus green, window lights, snow…). */
  horizonPalette: { dark: number; mid: number; light: number; accent: number };
  /** Clouds: how many of the 8 spots to use + opacity scale. */
  cloudCount: number;
  cloudOpacity: number;
}

export const CLASSIC_THEME: Putt3dTheme = {
  id: 'classic',
  skyTop: 0x55a3da,
  skyBottom: 0x7c93a1,
  fogColor: 0x7c93a1,
  background: 0x9fc4e3,
  sunColor: 0xfff7e0,
  sunPos: [-170, 250, -180],
  sunScale: 165,
  lightColor: 0xfff6e0,
  lightIntensity: 1.5,
  hemiSky: 0xdfeefb,
  hemiGround: 0x3a5c34,
  hemiIntensity: 1.05,
  greenTint: 0x8fcf72,
  roughTint: [0.62, 0.68, 0.44],
  fringeTint: [0.8, 0.84, 0.58],
  horizon: 'trees',
  horizonColor: 0x2e4f36,
  horizonPalette: { dark: 0x23402b, mid: 0x35583c, light: 0x4e7c52, accent: 0x4a3a2a },
  cloudCount: 8,
  cloudOpacity: 1.0,
};

export const THEMES: Record<string, Putt3dTheme> = {
  classic: CLASSIC_THEME,
  island: {
    ...CLASSIC_THEME,
    id: 'island',
    skyTop: 0x3f9fdc,
    skyBottom: 0x9fd4e8,
    fogColor: 0x9fd4e8,
    background: 0xaee0f2,
    sunColor: 0xfffbe8,
    lightIntensity: 1.6,
    greenTint: 0x93d877,
    roughTint: [0.9, 0.84, 0.58],
    fringeTint: [0.88, 0.88, 0.62],
    horizon: 'palms',
    horizonColor: 0x2f5e44,
    horizonPalette: { dark: 0x1f4a36, mid: 0x2f6b4a, light: 0x49915f, accent: 0x6b513a },
    cloudCount: 5,
    cloudOpacity: 0.8,
  },
  desert: {
    ...CLASSIC_THEME,
    id: 'desert',
    skyTop: 0x6aa7e0,
    skyBottom: 0xe3c49b,
    fogColor: 0xe3c49b,
    background: 0xd9b88f,
    sunColor: 0xffeccc,
    lightColor: 0xffeccc,
    lightIntensity: 1.7,
    greenTint: 0x9acb70,
    roughTint: [0.95, 0.8, 0.52],
    fringeTint: [0.88, 0.82, 0.56],
    horizon: 'mesa',
    horizonColor: 0x8a5a3c,
    horizonPalette: { dark: 0x6e4530, mid: 0x8a5a3c, light: 0xb07a4e, accent: 0x3f6b3c },
    cloudCount: 2,
    cloudOpacity: 0.6,
  },
  links: {
    ...CLASSIC_THEME,
    id: 'links',
    skyTop: 0x7da7c4,
    skyBottom: 0xb9c6c9,
    fogColor: 0xb9c6c9,
    background: 0xaebfc6,
    sunColor: 0xf6f2e4,
    lightColor: 0xf2efe2,
    lightIntensity: 1.25,
    greenTint: 0x88b76a,
    roughTint: [0.86, 0.81, 0.54],
    fringeTint: [0.84, 0.84, 0.6],
    horizon: 'dunes',
    horizonColor: 0x6f7f5e,
    horizonPalette: { dark: 0x5a6b4c, mid: 0x73855e, light: 0x93a474, accent: 0x7d8d66 },
    cloudCount: 8,
    cloudOpacity: 0.95,
  },
  city: {
    ...CLASSIC_THEME,
    id: 'city',
    skyTop: 0x5887b8,
    skyBottom: 0xa7b4bd,
    fogColor: 0xa7b4bd,
    background: 0x9fb1c0,
    sunColor: 0xfdf8ec,
    lightColor: 0xf4f4ff,
    lightIntensity: 1.35,
    greenTint: 0x84c46c,
    roughTint: [0.6, 0.64, 0.45],
    fringeTint: [0.78, 0.82, 0.58],
    horizon: 'skyline',
    horizonColor: 0x46535f,
    horizonPalette: { dark: 0x35414c, mid: 0x46535f, light: 0x5d6b78, accent: 0xd9c27a },
    cloudCount: 4,
    cloudOpacity: 0.85,
  },
  alpine: {
    ...CLASSIC_THEME,
    id: 'alpine',
    skyTop: 0x3d85d3,
    skyBottom: 0xa8c8de,
    fogColor: 0xa8c8de,
    background: 0xa6cae0,
    lightIntensity: 1.6,
    roughTint: [0.58, 0.66, 0.42],
    horizon: 'peaks',
    horizonColor: 0x5c6f7e,
    horizonPalette: { dark: 0x44566a, mid: 0x5c7186, light: 0xe8eef4, accent: 0x3a4a5c },
    cloudCount: 6,
    cloudOpacity: 0.9,
  },
  pine: {
    ...CLASSIC_THEME,
    id: 'pine',
    skyTop: 0x4d92c6,
    skyBottom: 0x90a8a4,
    fogColor: 0x90a8a4,
    background: 0x9cb6b4,
    greenTint: 0x7fbf68,
    roughTint: [0.55, 0.62, 0.4],
    fringeTint: [0.72, 0.78, 0.54],
    horizon: 'pines',
    horizonColor: 0x24402e,
    horizonPalette: { dark: 0x16301f, mid: 0x24402e, light: 0x32543c, accent: 0x46362a },
    cloudCount: 6,
    cloudOpacity: 0.9,
  },
  autumn: {
    ...CLASSIC_THEME,
    id: 'autumn',
    skyTop: 0x6f9bc8,
    skyBottom: 0xc9b18e,
    fogColor: 0xc9b18e,
    background: 0xc2ad90,
    sunColor: 0xffe9c4,
    lightColor: 0xffe9c4,
    lightIntensity: 1.4,
    greenTint: 0x9bc868,
    roughTint: [0.84, 0.72, 0.44],
    fringeTint: [0.86, 0.78, 0.52],
    horizon: 'trees',
    horizonColor: 0x7a5230,
    horizonPalette: { dark: 0x6e3d20, mid: 0x8a5230, light: 0xb06a36, accent: 0x4a3527 },
    cloudCount: 7,
    cloudOpacity: 0.9,
  },
  twilight: {
    ...CLASSIC_THEME,
    id: 'twilight',
    skyTop: 0x2c4a78,
    skyBottom: 0xd98a5e,
    fogColor: 0xd98a5e,
    background: 0x31507d,
    sunColor: 0xffd9a0,
    sunPos: [-220, 80, -260],
    sunScale: 220,
    lightColor: 0xffd2a0,
    lightIntensity: 1.2,
    hemiSky: 0x9db4d8,
    hemiGround: 0x3a4a34,
    hemiIntensity: 0.9,
    greenTint: 0x7dbb66,
    roughTint: [0.5, 0.55, 0.38],
    fringeTint: [0.68, 0.7, 0.5],
    horizon: 'trees',
    horizonColor: 0x1d2f3e,
    horizonPalette: { dark: 0x16242e, mid: 0x1d2f3e, light: 0x28404f, accent: 0x101c26 },
    cloudCount: 4,
    cloudOpacity: 0.7,
  },
  storm: {
    ...CLASSIC_THEME,
    id: 'storm',
    skyTop: 0x4a5a6e,
    skyBottom: 0x8d99a4,
    fogColor: 0x8d99a4,
    background: 0x86939e,
    sunColor: 0xe9edf2,
    lightColor: 0xdfe4ea,
    lightIntensity: 1.1,
    hemiSky: 0xc6d2dc,
    hemiIntensity: 0.95,
    greenTint: 0x7fb368,
    roughTint: [0.62, 0.66, 0.48],
    fringeTint: [0.74, 0.78, 0.56],
    horizon: 'dunes',
    horizonColor: 0x55636a,
    horizonPalette: { dark: 0x44525a, mid: 0x55636a, light: 0x6b7a80, accent: 0x4a565e },
    cloudCount: 8,
    cloudOpacity: 1.0,
  },
  cedar: {
    ...CLASSIC_THEME,
    id: 'cedar',
    visualStyle: 'realistic',
    // Warm late-afternoon light keeps the PBR turf readable without
    // requiring mobile-expensive post-processing or environment reflections.
    skyTop: 0x367fb9,
    skyBottom: 0xc0d8df,
    fogColor: 0xc0d8df,
    background: 0xa8c1cf,
    sunColor: 0xffedc7,
    sunPos: [-210, 170, -190],
    sunScale: 128,
    lightColor: 0xfff0d4,
    lightIntensity: 2.65,
    hemiSky: 0xe2eff8,
    hemiGround: 0x516847,
    hemiIntensity: 1.05,
    // The diffuse map carries most of the color. This mild tint
    // prevents the texture from being multiplied into the neon-green range.
    greenTint: 0xd7ffd7,
    roughTint: [0.49, 0.68, 0.47],
    fringeTint: [0.71, 0.85, 0.63],
    horizon: 'pines',
    horizonColor: 0x263e2d,
    horizonPalette: {
      dark: 0x17291e,
      mid: 0x294333,
      light: 0x45634b,
      accent: 0x4d3828,
    },
    cloudCount: 2,
    cloudOpacity: 0.42,
  },
};

export function themeById(id: string | undefined): Putt3dTheme {
  return (id && THEMES[id]) || CLASSIC_THEME;
}
